import React from 'react';
import { connect } from 'react-redux';
import {
  Breadcrumb,
  BreadcrumbButton,
  BreadcrumbDivider,
  BreadcrumbItem,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  SearchBox,
  Skeleton,
  SkeletonItem,
  Spinner,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideHelpText } from '../../../theme/components/GuideField';
import {
  CheckmarkCircle16Regular,
  Dismiss24Regular,
  ErrorCircle16Regular,
  Eye20Regular,
  Folder20Regular,
  FolderOpen24Regular,
  Info16Regular,
  Link20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import * as ComponentFunctions from './FilePickerDialog.Functions';
import { useFilePickerDialogStyles } from './FilePickerDialog.Styles';
import { buildExplorerBreadcrumbs } from '../FilesExplorer/FilesExplorer.Functions';
import FilePreview from '../FilePreview/FilePreview.Component';
import { PreviewFile } from '../FilePreview/FilePreview.Functions';
import { formatFileSize } from '../../../functions/AzureFileUpload.Functions';
import {
  Translate,
  collectStorageEntityRefs,
  getFriendlyFolderName,
  getStorageFileExtension,
  matchesStorageSearch,
} from '../../../functions/StorageBrowser.Functions';
import { StorageBrowseViewModel, StorageFileViewModel } from '../../../models/Entities/AzureFiles/StorageBrowser';
import { ErrorType } from '../../../models/HttpError';

const resultBadge: Record<
  ComponentFunctions.PickerLinkResult['status'],
  { color: 'success' | 'warning' | 'danger'; icon: JSX.Element; labelKey: string }
> = {
  linked: { color: 'success', icon: <CheckmarkCircle16Regular />, labelKey: 'filePicker.results.linked' },
  alreadyLinked: { color: 'warning', icon: <Info16Regular />, labelKey: 'filePicker.results.alreadyLinked' },
  error: { color: 'danger', icon: <ErrorCircle16Regular />, labelKey: 'filePicker.results.error' },
};

/**
 * Explorer in picker mode (contract §9.3/§9.4): navigate folders, search in the folder, select several
 * files (the selection survives folder changes), preview them and link them one by one with a result per file.
 */
export const FilePickerDialogComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const translate = t as unknown as Translate;
  const styles = useFilePickerDialogStyles();
  const { open, BrowsePickerFolder, ResolveEntityNames } = props;

  const [path, setPath] = React.useState(props.initialPath ?? '');
  const [listing, setListing] = React.useState<StorageBrowseViewModel | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState<ErrorType | undefined>(undefined);
  const [search, setSearch] = React.useState('');
  const [selected, setSelected] = React.useState<StorageFileViewModel[]>([]);
  const [linking, setLinking] = React.useState(false);
  const [results, setResults] = React.useState<ComponentFunctions.PickerLinkResult[]>([]);
  const [previewFile, setPreviewFile] = React.useState<PreviewFile | undefined>(undefined);
  const [attempt, setAttempt] = React.useState(0);
  const mounted = React.useRef(true);

  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Every time it opens: start again from the initial folder with an empty selection.
  React.useEffect(() => {
    if (!open) return;
    setPath(props.initialPath ?? '');
    setSelected([]);
    setResults([]);
    setSearch('');
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    setListing(undefined);
    setSearch('');
    BrowsePickerFolder(path)
      .then((result) => {
        if (cancelled || !mounted.current) return;
        setListing(result);
        setLoading(false);
        const refs = collectStorageEntityRefs([path, ...result.folders.map((folder) => folder.path)]);
        if (refs.length > 0) ResolveEntityNames(refs);
      })
      .catch((failure: ErrorType) => {
        if (cancelled || !mounted.current) return;
        setError(failure);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, path, attempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    if (!listing?.continuationToken || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await BrowsePickerFolder(path, listing.continuationToken);
      if (!mounted.current) return;
      setListing((current) =>
        current
          ? {
              ...current,
              folders: [...current.folders, ...next.folders],
              files: [...current.files, ...next.files],
              continuationToken: next.continuationToken,
            }
          : next
      );
    } catch (failure: unknown) {
      if (mounted.current) setError(failure as ErrorType);
    } finally {
      if (mounted.current) setLoadingMore(false);
    }
  };

  const navigate = (next: string) => {
    if (linking || next === path) return;
    setPath(next);
  };

  const isSelected = (file: StorageFileViewModel) => selected.some((item) => item.path === file.path);
  const toggle = (file: StorageFileViewModel, checked: boolean) =>
    setSelected((current) =>
      checked ? (current.some((item) => item.path === file.path) ? current : [...current, file]) : current.filter((item) => item.path !== file.path)
    );

  const link = async () => {
    if (linking || selected.length === 0) return;
    setLinking(true);
    setResults([]);
    const outcome = await ComponentFunctions.linkSequentially(selected, props.linkFile, (result) => {
      if (mounted.current) setResults((current) => [...current, result]);
    });
    if (!mounted.current) return;
    setLinking(false);
    // Linked and already linked files leave the selection; failures stay selected for a retry.
    setSelected((current) => current.filter((file) => outcome.some((r) => r.path === file.path && r.status === 'error')));
    const linkedCount = outcome.filter((result) => result.status === 'linked').length;
    if (linkedCount > 0) props.onLinked(linkedCount);
  };

  const close = () => {
    if (!linking) props.onDismiss();
  };

  const breadcrumbs = buildExplorerBreadcrumbs(
    listing?.breadcrumbs ?? [],
    path,
    t('filesExplorer.root'),
    translate,
    props.ownerNames
  );
  const folders = (listing?.folders ?? []).filter((folder) =>
    matchesStorageSearch([folder.name, getFriendlyFolderName(folder.name, translate, props.ownerNames)], search)
  );
  const files = ComponentFunctions.filterPickerFiles(listing?.files ?? [], props.allowedExtensions).filter((file) =>
    matchesStorageSearch([file.name], search)
  );
  const isEmpty = !loading && !error && folders.length === 0 && files.length === 0;

  const renderList = () => {
    if (loading) {
      return (
        <Skeleton aria-label={t('filesExplorer.messages.loading')} className={styles.skeleton} data-testid="file-picker-skeleton">
          <SkeletonItem size={16} />
          <SkeletonItem size={16} />
          <SkeletonItem size={16} />
        </Skeleton>
      );
    }
    if (error) return null;
    if (isEmpty) {
      return (
        <div className={styles.emptyState} data-testid="file-picker-empty">
          <FolderOpen24Regular aria-hidden />
          <span className={styles.emptyTitle}>
            {search.trim() ? t('filesExplorer.empty.noResultsTitle', { search: search.trim() }) : t('filesExplorer.empty.title')}
          </span>
          <span>{t('filePicker.emptyHelp')}</span>
        </div>
      );
    }
    return (
      <ul className={styles.list} aria-label={t('filePicker.listLabel')} data-testid="file-picker-list">
        {folders.map((folder) => {
          const displayName = getFriendlyFolderName(folder.name, translate, props.ownerNames);
          return (
            <li key={`folder:${folder.path}`} className={styles.row} data-testid="file-picker-folder">
              <Folder20Regular className={styles.folderIcon} aria-hidden />
              <Tooltip content={folder.name} relationship="description" withArrow>
                <Button
                  appearance="transparent"
                  className={styles.folderButton}
                  onClick={() => navigate(folder.path)}
                  disabled={linking}
                  aria-label={t('filesExplorer.actions.openNamed', { name: displayName })}
                >
                  {displayName}
                </Button>
              </Tooltip>
            </li>
          );
        })}
        {files.map((file) => {
          const checked = isSelected(file);
          return (
            <li
              key={`file:${file.path}`}
              className={mergeClasses(styles.row, checked && styles.rowSelected)}
              data-testid="file-picker-file"
            >
              <Checkbox
                className={styles.fileCheckbox}
                checked={checked}
                disabled={linking}
                onChange={(_, data) => toggle(file, !!data.checked)}
                label={file.name}
                data-testid="file-picker-checkbox"
              />
              <span className={styles.meta}>{file.lastModified ? moment(file.lastModified).format('DD/MM/YYYY HH:mm') : '—'}</span>
              <span className={styles.meta}>{formatFileSize(file.size)}</span>
              <Button
                appearance="subtle"
                size="small"
                icon={<Eye20Regular />}
                onClick={() =>
                  setPreviewFile({
                    path: file.path,
                    name: file.name,
                    extension: getStorageFileExtension(file),
                    size: file.size,
                    lastModified: file.lastModified,
                  })
                }
                aria-label={t('filesExplorer.actions.previewNamed', { name: file.name })}
                title={t('filesExplorer.actions.preview')}
                data-testid="file-picker-preview"
              />
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(_, data) => {
          if (!data.open) close();
        }}
      >
        <DialogSurface className={styles.surface} data-testid="file-picker-dialog">
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label={t('filesExplorer.actions.close')}
                  icon={<Dismiss24Regular />}
                  onClick={close}
                  disabled={linking}
                />
              }
            >
              {props.title}
            </DialogTitle>
            <DialogContent className={styles.content}>
              <div className={styles.topRow}>
                <Breadcrumb className={styles.breadcrumb} aria-label={t('filesExplorer.breadcrumbLabel')} data-testid="file-picker-breadcrumb">
                  {breadcrumbs.map((crumb, index) => {
                    const isCurrent = index === breadcrumbs.length - 1;
                    return (
                      <React.Fragment key={crumb.key}>
                        <BreadcrumbItem>
                          <BreadcrumbButton
                            current={isCurrent}
                            onClick={isCurrent ? undefined : () => navigate(crumb.path)}
                            data-testid={`file-picker-breadcrumb-${index}`}
                          >
                            {crumb.displayName}
                          </BreadcrumbButton>
                        </BreadcrumbItem>
                        {!isCurrent && <BreadcrumbDivider />}
                      </React.Fragment>
                    );
                  })}
                </Breadcrumb>
                <SearchBox
                  className={styles.search}
                  value={search}
                  onChange={(_, data) => setSearch(data.value)}
                  placeholder={t('filesExplorer.searchPlaceholder')}
                  aria-label={t('filesExplorer.searchPlaceholder')}
                  data-testid="file-picker-search"
                />
              </div>

              {props.allowedExtensions && props.allowedExtensions.length > 0 && (
                <GuideHelpText data-testid="file-picker-extensions">
                  {t('filePicker.onlyExtensions', { extensions: props.allowedExtensions.join(', ') })}
                </GuideHelpText>
              )}

              {error && (
                <GuideMessageBar intent="error" layout="multiline" data-testid="file-picker-error">
                  <MessageBarBody>
                    <MessageBarTitle>{t('filesExplorer.messages.loadErrorTitle')}</MessageBarTitle>
                    {error.ErrorMessage || t('filesExplorer.messages.loadError')}
                  </MessageBarBody>
                  <MessageBarActions>
                    <Button appearance="transparent" size="small" onClick={() => setAttempt((value) => value + 1)}>
                      {t('filesExplorer.actions.retry')}
                    </Button>
                  </MessageBarActions>
                </GuideMessageBar>
              )}

              {renderList()}

              {listing?.continuationToken && !loading && (
                <div className={styles.loadMore}>
                  <Button
                    appearance="secondary"
                    onClick={loadMore}
                    disabled={loadingMore}
                    icon={loadingMore ? <Spinner size="extra-tiny" /> : undefined}
                  >
                    {t('filesExplorer.actions.loadMore')}
                  </Button>
                </div>
              )}

              <span className={styles.summary} aria-live="polite" data-testid="file-picker-selected">
                {t('filePicker.selected', { count: selected.length })}
              </span>

              {results.length > 0 && (
                <ul className={styles.results} aria-live="polite" data-testid="file-picker-results">
                  {results.map((result) => (
                    <li key={result.path} className={styles.resultRow} data-testid={`file-picker-result-${result.status}`}>
                      <StatusPill
                        color={resultBadge[result.status].color}
                        icon={resultBadge[result.status].icon}
                      >
                        {t(resultBadge[result.status].labelKey)}
                      </StatusPill>
                      <span className={styles.resultName}>{result.name}</span>
                      {result.status === 'error' && result.message && (
                        <span className={styles.resultMessage}>{result.message}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={close} disabled={linking}>
                {results.length > 0 ? t('filesExplorer.actions.close') : t('filesExplorer.actions.cancel')}
              </Button>
              <Button
                appearance="primary"
                icon={linking ? <Spinner size="extra-tiny" /> : <Link20Regular />}
                onClick={link}
                disabled={linking || selected.length === 0}
                data-testid="file-picker-link"
              >
                {t('filePicker.linkButton', { count: selected.length })}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      <FilePreview open={!!previewFile} file={previewFile} onDismiss={() => setPreviewFile(undefined)} />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(FilePickerDialogComponent as React.ComponentType<ComponentFunctions.Props>);
